import { useRouter } from 'next/router';
import { isNil } from 'ramda';
import { useCallback } from 'react';
import {
  AppsOpenedEvent,
  CollapseWidgetEvent,
  DHApp,
  DeepLinkEvent,
  DismissWidgetEvent,
  ExpandWidgetEvent,
  OpenCommandEvent,
  TrackingEvent,
  VirtualPageViewEvent,
} from '../../shared/types';
import isAppPath from '../../utils/isAppPath';
import gtag from '../../utils/gtag';
import useWidgetContext from '../useWidgetContext';

export const FIXED_WGT_EVENT_PREFIX = 'dh.wgt';

const useFireTrackingEvent = () => {
  const { apps } = useWidgetContext();
  const router = useRouter();

  // Any context from hooks can be provided here
  const fireEvent = useCallback(
    (event: TrackingEvent) => {
      switch (event.event) {
        case 'VirtualPageView':
          return onPageView(event, apps, router.pathname);
        case 'ExpandWidget':
          return onExpandWidget(event);
        case 'DeepLink':
          return onDeepLink(event);
        case 'OpenCommand':
          return onOpenByCommand(event);
        case 'CollapseWidget':
          return onCollapseWidget(event, router.pathname);
        case 'DismissWidget':
          return onDismissWidget(event, router.pathname);
        case 'AppsOpened':
          return onAppsOpened(event, router.pathname);

        default:
          return gtag('event', 'unhandled_event', {
            event_category: 'unhandled',
            event_label: 'Unhandled Event',
            payload: event,
          });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return fireEvent;
};

const onPageView = (
  event: VirtualPageViewEvent,
  apps: Record<DHApp['slug'], DHApp>,
  pathname: string,
) => {
  const app = isAppPath(event.path)
    ? apps[event.path.replace(/^\/apps\//, '')]
    : null;

  if (!isNil(app)) {
    return gtag('event', 'page_view', {
      page_title: app.title.nl,
      page_location: pathname,
      page_path: event.path,
    });
  }

  return gtag('event', 'page_view', {
    page_title: document.title,
    page_location: pathname,
    page_path: event.path,
  });
};

const onCollapseWidget = (event: CollapseWidgetEvent, pathname: string) =>
  gtag('event', `${FIXED_WGT_EVENT_PREFIX}.engagement.collapse_widget`, {
    event_category: 'interaction',
    event_label: 'Collapse Widget',
    path: pathname,
  });

const onExpandWidget = (event: ExpandWidgetEvent) =>
  gtag('event', `${FIXED_WGT_EVENT_PREFIX}.engagement.expand_widget`, {
    event_category: 'interaction',
    event_label: 'Expand Widget',
    path: event.path,
    trigger: event.triggerType,
  });

const onDeepLink = (event: DeepLinkEvent) =>
  gtag('event', `${FIXED_WGT_EVENT_PREFIX}.engagement.opened_using_deep_link`, {
    event_category: 'interaction',
    event_label: 'Deep Link',
    path: event.path,
    trigger: event.triggerType,
  });

const onOpenByCommand = (event: OpenCommandEvent) =>
  gtag('event', `${FIXED_WGT_EVENT_PREFIX}.engagement.opened_using_command`, {
    event_category: 'interaction',
    event_label: 'Open Command',
    path: event.path,
    trigger: event.triggerType,
  });

const onDismissWidget = (event: DismissWidgetEvent, pathname: string) =>
  gtag('event', `${FIXED_WGT_EVENT_PREFIX}.engagement.dismiss_widget`, {
    event_category: 'interaction',
    event_label: 'Dismiss Widget',
    path: pathname,
  });

const onAppsOpened = (event: AppsOpenedEvent, pathname: string) =>
  gtag(
    'event',
    `${FIXED_WGT_EVENT_PREFIX}.engagement.opened_${event.amount}_apps`,
    {
      event_category: 'interaction',
      event_label: `${event.amount} apps opened`,
      path: pathname,
    },
  );

export default useFireTrackingEvent;
